import ServiceStore from "./ServiceStore";
import { OfflineServiceStore } from "./OfflineServiceStore";
import type RootStore from "./RootStore";

export interface ServiceStoreFactoryOptions {
  enableOffline?: boolean;
  conflictStrategy?: "client" | "server" | "merge" | "manual";
  cacheTTL?: number;
  syncOnReconnect?: boolean;
  [key: string]: any;
}

/**
 * Factory to create ServiceStore instances with optional offline capabilities
 * This allows gradual migration of existing stores to offline-first architecture
 */
export class ServiceStoreFactory {
  static create(
    rootStore: RootStore,
    serviceName: string,
    options: ServiceStoreFactoryOptions = {}
  ) {
    // Check if offline is explicitly enabled for this store
    const enableOffline = options.enableOffline ?? false;

    // List of stores to automatically enable offline for (start with non-critical stores)
    // NOTE: do NOT add "notifications" here. The Notification Center service at
    // /notifications is socket-driven and crashes the OfflineServiceStore path
    // (double makeAutoObservable on a subclass). Keep notifications on the plain
    // ServiceStore.
    const autoOfflineStores = [
      "settings",
      "user-preferences",
      "logs",
      "refresh-token",
    ];

    // Check if this store should have offline enabled automatically
    const shouldEnableOffline =
      enableOffline ||
      autoOfflineStores.includes(serviceName) ||
      this.isFeatureFlagEnabled("offline_all_stores");

    if (shouldEnableOffline) {
      // Create offline-capable store
      const offlineOptions = {
        ...options,
        offline: {
          enableOffline: true,
          conflictStrategy: options.conflictStrategy || "server",
          cacheTTL: options.cacheTTL || 5 * 60 * 1000, // 5 minutes default
          syncOnReconnect: options.syncOnReconnect ?? true,
        },
      };

      console.log(`Creating OfflineServiceStore for ${serviceName}`);
      return new OfflineServiceStore(rootStore, serviceName, offlineOptions);
    } else {
      // Create standard store
      // console.log(`Creating standard ServiceStore for ${serviceName}`);
      return new ServiceStore(rootStore, serviceName, options as any);
    }
  }

  /**
   * Check if a feature flag is enabled
   * This can be extended to check localStorage, environment variables, or remote config
   */
  private static isFeatureFlagEnabled(flag: string): boolean {
    // Check localStorage for feature flags
    try {
      const flags = localStorage.getItem("featureFlags");
      if (flags) {
        const parsedFlags = JSON.parse(flags);
        return parsedFlags[flag] === true;
      }
    } catch (e) {
      // Ignore errors
    }

    // Check environment variables
    if (typeof process !== "undefined" && process.env) {
      return process.env[`VITE_FEATURE_${flag.toUpperCase()}`] === "true";
    }

    return false;
  }

  /**
   * Enable offline mode for a specific store type globally
   */
  static enableOfflineForStore(storeName: string) {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      const offlineStores = JSON.parse(flags);
      if (!offlineStores.includes(storeName)) {
        offlineStores.push(storeName);
        localStorage.setItem("offlineStores", JSON.stringify(offlineStores));
      }
    } catch (e) {
      console.error("Failed to enable offline for store:", e);
    }
  }

  /**
   * Disable offline mode for a specific store type globally
   */
  static disableOfflineForStore(storeName: string) {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      const offlineStores = JSON.parse(flags);
      const index = offlineStores.indexOf(storeName);
      if (index > -1) {
        offlineStores.splice(index, 1);
        localStorage.setItem("offlineStores", JSON.stringify(offlineStores));
      }
    } catch (e) {
      console.error("Failed to disable offline for store:", e);
    }
  }

  /**
   * Get list of stores with offline enabled
   */
  static getOfflineStores(): string[] {
    try {
      const flags = localStorage.getItem("offlineStores") || "[]";
      return JSON.parse(flags);
    } catch (e) {
      return [];
    }
  }
}
